// Theme control, in the top bar after the notification bell.
//
// An explicit three-option menu rather than a button that cycles: the states
// are System / Light / Dark, and a cycling control makes you press it up to
// three times to reach the one you want while never showing you what the
// options are. Picking from a list is one press and self-describing.
//
// Follows the ProfileMenu popover pattern (outside-click + Escape to close) so
// the three controls in this corner behave the same way.
import { useEffect, useRef, useState } from 'react';
import { Icon, type IconName } from '@/components/garden';
import { useTheme } from '@/hooks/useTheme';
import { THEME_LABEL, THEME_ORDER, type ThemePreference } from '@/lib/theme';

const ICON: Record<ThemePreference, IconName> = {
  system: 'monitor',
  light: 'sun',
  dark: 'moon',
};

const HINT: Record<ThemePreference, string> = {
  system: 'Match your device',
  light: 'Always light',
  dark: 'Easier at night',
};

export function ThemeToggle() {
  const { preference, resolved, setPreference } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const description =
    preference === 'system'
      ? `System (${THEME_LABEL[resolved].toLowerCase()})`
      : THEME_LABEL[preference];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={`Theme: ${description}`}
        aria-label={`Theme: ${description}. Change theme.`}
        aria-haspopup="menu"
        aria-expanded={open}
        className="rounded-full border border-line bg-card p-1.5 text-ink-soft hover:border-forest hover:text-forest"
        style={{
          display: 'inline-flex',
          transition:
            'color var(--dur-fast) var(--ease-standard), border-color var(--dur-fast) var(--ease-standard)',
        }}
      >
        <Icon name={ICON[preference]} size={16} />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Theme"
          style={{
            position: 'absolute',
            zIndex: 50,
            top: 'calc(100% + 8px)',
            right: 0,
            minWidth: 200,
            background: 'var(--surface-card)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-lg)',
            overflow: 'hidden',
            padding: '4px',
          }}
        >
          {THEME_ORDER.map((option) => (
            <ThemeOption
              key={option}
              option={option}
              selected={option === preference}
              onSelect={() => {
                setPreference(option);
                setOpen(false);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ThemeOption({
  option,
  selected,
  onSelect,
}: {
  option: ThemePreference;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      role="menuitemradio"
      aria-checked={selected}
      // The visible label is split across nested spans and the tick is
      // aria-hidden, which left the option with no accessible name at all —
      // a screen reader announced three unlabelled radios.
      aria-label={`${THEME_LABEL[option]} — ${HINT[option]}`}
      onClick={onSelect}
      className="cwy-menu-option"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        width: '100%',
        textAlign: 'left',
        padding: '9px 10px',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        background: selected ? 'var(--surface-sunk)' : 'transparent',
        color: 'var(--text-body)',
      }}
    >
      <Icon name={ICON[option]} size={16} style={{ flex: 'none', opacity: 0.85 }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 600 }}>
          {THEME_LABEL[option]}
        </span>
        <span style={{ display: 'block', fontSize: 'var(--text-2xs)', color: 'var(--text-hint)' }}>
          {HINT[option]}
        </span>
      </span>
      {/* A tick, not colour alone, marks the active option. */}
      <span
        aria-hidden
        style={{
          flex: 'none',
          width: 14,
          color: 'var(--text-accent)',
          visibility: selected ? 'visible' : 'hidden',
        }}
      >
        ✓
      </span>
    </button>
  );
}
